import { Color } from '@/constants/color';
import { BillingReportRow } from '@/pages/BillingReportResults/types';

import { AffectUser } from './affectUser';
import { Appointment, AppointmentAttendance } from './appointments';
import { DrugScreen } from './drugScreen';
import { InsurancePolicy } from './insurancePolicy';
import { PersonMinimal } from './person';

export enum ENCOUNTER_TYPES {
  APPOINTMENT = 'appointment',
  APPOINTMENT_ATTENDANCE = 'appointment_attendance',
  DRUG_SCREEN = 'drug_screen',
  FORM_ANSWER_GROUP = 'form_answer_group',
  CASE_RATE_BILLING_PERIOD = 'case_rate_billing_period',
  ADD_ON = 'add_on',
  ADDITIONAL_SERVICE = 'additional_service'
}

export enum ENCOUNTER_TYPES_API {
  APPOINTMENT = 'appointment',
  APPOINTMENT_ATTENDANCE = 'appointmentattendance',
  DRUG_SCREEN = 'drugscreen',
  FORM_ANSWER_GROUP = 'formanswergroup',
  CASE_RATE_BILLING_PERIOD = 'case_rate_billing_period',
  ADD_ON = 'add_on',
  ADDITIONAL_SERVICE = 'additional_service'
}

export enum ENCOUNTER_APP_TYPES_API {
  APPOINTMENT = 'appointments.appointment',
  APPOINTMENT_ATTENDANCE = 'appointments.appointmentattendance',
  DRUG_SCREEN = 'drugscreens.drugscreen',
  FORM_ANSWER_GROUP = 'healthie.formanswergroup',
  CASE_RATE_BILLING_PERIOD = 'billing.case_rate_billing_period',
  ADD_ON = 'billing.add_on',
  ADDITIONAL_SERVICE = 'healthie.formanswer'
}

export const isEncounterTypeValid = (encounterType: string) =>
  Object.values(ENCOUNTER_TYPES).includes(encounterType as ENCOUNTER_TYPES);

export const encounterTypeMapping = {
  [ENCOUNTER_TYPES_API.APPOINTMENT]: {
    label: 'Appointment',
    path: ENCOUNTER_TYPES.APPOINTMENT
  },
  [ENCOUNTER_TYPES_API.APPOINTMENT_ATTENDANCE]: {
    label: 'Appointment Attendance',
    path: ENCOUNTER_TYPES.APPOINTMENT_ATTENDANCE
  },
  [ENCOUNTER_TYPES_API.FORM_ANSWER_GROUP]: {
    label: 'Chart',
    path: ENCOUNTER_TYPES.FORM_ANSWER_GROUP
  },
  [ENCOUNTER_TYPES_API.DRUG_SCREEN]: {
    label: 'Drug Screen',
    path: ENCOUNTER_TYPES.DRUG_SCREEN
  },
  [ENCOUNTER_TYPES_API.CASE_RATE_BILLING_PERIOD]: {
    label: 'Case Rate Billing Period',
    path: ENCOUNTER_TYPES.CASE_RATE_BILLING_PERIOD
  },
  [ENCOUNTER_TYPES_API.ADD_ON]: {
    label: 'Add On',
    path: ENCOUNTER_TYPES.ADD_ON
  },
  [ENCOUNTER_TYPES_API.ADDITIONAL_SERVICE]: {
    label: 'Additional Service',
    path: ENCOUNTER_TYPES.ADDITIONAL_SERVICE
  }
};

export type EncounterType = ENCOUNTER_TYPES_API | string; // string is a guard against a potential missing enum value

export interface Encounter {
  appointment?: Appointment;
  appointment_attendance?: AppointmentAttendance;
  billing_item: BillingItem | null;
  charts?: EncounterChart[];
  deleted_at?: string | null;
  drug_screens?: DrugScreen[];
  encounter_id: string;
  encounter_type: EncounterType;
  healthie_id: string;
  maybe_billable: boolean;
  name: string;
  person: PersonMinimal | null;
  related_encounters: Encounter[];
}

// Summaries serialize healthie_id as a number, and omit it for drug screens.
export type EncounterSummary = Pick<
  Encounter,
  'encounter_id' | 'encounter_type' | 'maybe_billable' | 'name'
> & {
  healthie_id?: number | null;
  person?: PersonMinimal | null;
  appointment_attendance?: { appointment: Pick<Appointment, 'id'> } | null;
};

export type EncounterLookupResponse = EncounterSummary & {
  healthie_id: number;
  related_encounters: EncounterSummary[];
};

export interface EncounterChart {
  id: string;
  healthie_id: number;
  healthie_name: string | null;
  healthie_created_at: string | null;
  healthie_locked_at: string | null;
  filled_by: string | null;
  locked_by: string | null;
  is_locked: boolean;
  directly_linked: boolean;
  is_billing_chart: boolean;
  external_links: {
    homebase: { url: string };
    healthie: { url: string | null };
  };
}

export interface BillingItem {
  billing_report: {
    id: string;
  } | null;
  healthie_claim_rows: Cms1500Cptcode[];
  cached_at: string | null;
  created_at: string;
  forced_valid_at: string | null;
  forced_valid_by: AffectUser | null;
  id: string;
  manually_associated_at: string | null;
  manually_associated_by: AffectUser | null;
  non_billable_at: string | null;
  non_billable_by: AffectUser | null;
  non_billable_reason: NonBillableReason | null;
  flags: BillingItemFlag[];
  billing_item_edits: BillingItemEdit[];
  active_detected_alerts: DetectedAlert[];
  row?: BillingReportRow;
}

export interface BillingItemEdit {
  id: string;
  at: string;
  by: AffectUser;
  notes: string;
  field_edits: BillingItemFieldEdit[];
}

export interface BillingItemFieldEdit {
  id: number;
  edit_type: string;
  value: string | string[];
}

export interface DetectedAlert {
  id: number;
  detected_at: string;
  level: string;
  group: string;
  description: string;
}

export interface Alert {
  id: number;
  group: string;
  description: string;
}

export interface BillingItemFlag {
  id: string;
  at: string;
  by: AffectUser;
  notes: string | null;
  reason: string | null;
  type: string;
  ignored_alerts?: Alert[];
}

export interface BillingItemMinimal {
  id: string;
  encounter_id: string;
  encounter_type: ENCOUNTER_APP_TYPES_API | string;
  service_at: string | null;
  service_type: string | null;
  service_class: string | null;
  person_name: string | null;
}

export interface Cms1500Cptcode {
  cms1500: Cms1500 | null;
  cpt_code_code: string;
  cpt_code_description: string;
  created_at: string;
  epsdt: string;
  family_planning_service: boolean;
  fee_in_cents: number;
  first_sent_to_clearinghouse_at: string | null;
  healthie_id: number;
  id: string;
  last_sent_to_clearinghouse_at: string | null;
  mod1: string;
  mod2: string;
  mod3: string;
  mod4: string;
  service_date: string;
  units: number;
  updated_at: string;
  billing_items: BillingItemMinimal[];
}

export interface Cms1500 {
  additional_text: string | null;
  created_at: string;
  date_of_service: string;
  first_sent_to_clearinghouse_at: string | null;
  healthie_created_at: string;
  healthie_id: number;
  candid_encounter_ids: string[] | null;
  id: string;
  healthie_amount_paid_in_cents: number;
  healthie_amount_reimbursed_in_cents: number;
  healthie_client_responsibility_in_cents: number;
  healthie_copay_still_owed_in_cents: number;
  healthie_copay_in_cents: number;
  last_sent_to_clearinghouse_at: string | null;
  nineteen_reserved: string | null;
  orig_ref_number: string | null;
  patient_account_num: string | null;
  patient_birthday: string;
  patient_full_name: string;
  patient_jurisdiction: string;
  patient: string;
  primary_plan_name: string;
  rendering_provider_name_with_title: string;
  resubmission_code: string | null;
  status: Cms1500Status;
  updated_at: string;
  cms1500cptcodes: Cms1500Cptcode[];
  policies?: InsurancePolicy[];
}

export enum Cms1500Status {
  CLIENT_DEDUCTIBLE_APPLIES = "Client's Deductible Applies",
  CLOSED = 'Closed',
  DENIED_BY_INSURANCE = 'Denied by Insurance',
  FULLY_PAID = 'Fully Paid',
  NOT_SENT = 'Not Sent',
  PARTIALLY_PAID = 'Partially Paid',
  REJECT_BY_CLEARINGHOUSE = 'Reject by Clearinghouse'
}

export const Cms1500StatusColor = {
  [Cms1500Status.CLIENT_DEDUCTIBLE_APPLIES]: Color.BLUE,
  [Cms1500Status.CLOSED]: Color.GREEN,
  [Cms1500Status.DENIED_BY_INSURANCE]: Color.RED,
  [Cms1500Status.FULLY_PAID]: Color.GREEN,
  [Cms1500Status.NOT_SENT]: Color.BLUE,
  [Cms1500Status.PARTIALLY_PAID]: Color.YELLOW,
  [Cms1500Status.REJECT_BY_CLEARINGHOUSE]: Color.RED
};

export interface NonBillableReason {
  id: string;
  name: string;
}

export interface MarkNonBillablePayload {
  non_billable_reason_id: string;
  note?: string;
}

export interface EncounterChecklistError {
  key: string;
  message: string;
}

export interface EncounterChecklistItem {
  key: string;
  label: string;
  // Null when it can't be determined yet: a manual step, or checks still pending
  is_complete: boolean | null;
  // Waiting on billing data that doesn't exist yet
  is_pending: boolean;
  errors: EncounterChecklistError[];
}
